// ========== ENVIRONMENT ==========
    // The same file runs locally (Express server, npm start) and as a Shopify page (build-shopify.mjs
    // copies it into the theme). On Shopify: events go to Shopify analytics, links stay on the shop
    // domain and images come from the theme assets (window.QUIZ_ASSETS, set by the page template).
    const ON_SHOPIFY = !!(window.Shopify && window.Shopify.shop);
    const SHOP_URL = ON_SHOPIFY ? '' : 'https://doglab.pl';
    function quizAsset(file) {
      return (window.QUIZ_ASSETS && window.QUIZ_ASSETS[file]) || '/' + file;
    }

    // ========== ANALYTICS (funnel tracking) ==========
    // On Shopify: custom events (quiz_view, quiz_form_start, …) for Customer events / pixels.
    // web-pixels-manager may not be ready yet on the first event – then publish on load.
    function publishToShopify(type, meta) {
      const name = type.startsWith('quiz_') ? type : 'quiz_' + type;
      const send = () => {
        try { window.Shopify.analytics.publish(name, meta || {}); return true; } catch { return false; }
      };
      if (!(window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) || !send()) {
        window.addEventListener('load', send, { once: true });
      }
    }

    // localStorage can throw (blocked storage, some in-app browsers) – that must not stop the quiz
    function getSessionId() {
      let id = null;
      try { id = localStorage.getItem('zj_sid'); } catch {}
      if (!id) {
        id = 'sid_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        try { localStorage.setItem('zj_sid', id); } catch {}
      }
      return id;
    }
    const SESSION_ID = getSessionId();
    const _tracked = new Set();
    function track(type, meta, once) {
      if (once) {
        if (_tracked.has(type)) return;
        _tracked.add(type);
      }
      if (ON_SHOPIFY) { publishToShopify(type, meta); return; }
      try {
        fetch('/api/event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type, sessionId: SESSION_ID, meta: meta || null }),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    }
    // Fire 'view' once per page load
    track('view', { ref: document.referrer || null }, true);

    // ========== CLOSE / EXIT FLOW ==========
    function getCurrentScreen() {
      // Returns: 'hero' | 'step_N' | 'result'
      if (document.getElementById('report')?.classList.contains('visible')) return 'result';
      if (!_started) return 'hero';
      return `step_${currentStep}`;
    }

    // Close (X) takes the visitor to the product page – no confirmation, never closes the tab.
    // The short delay lets the exit event go out before the page unloads.
    function doClose() {
      track('quiz_exit', { screen: getCurrentScreen() });
      setTimeout(() => {
        window.location.href = `${SHOP_URL}/products/probiotyk`;
      }, 150);
    }
    document.getElementById('closeBtn')?.addEventListener('click', doClose);

    // ========== STATE (single source of truth) ==========
    const state = {
      dog_name: '',
      skin_issues: [],         // Q1 (multi)
      other_problems: [],      // Q2 (multi)
      dog_weight: '',          // Q3 (single): do_10 | 10_30 | pow_30
    };

    function dogName() { return (state.dog_name || '').trim() || 'Twój pies'; }

    function refreshDogNameMentions() {
      const name = (state.dog_name || '').trim();
      document.querySelectorAll('.dog-name').forEach(el => {
        // Use saved fallback if no name, else inject name (most Polish names are indeclinable
        // in this context: "Mochi", "Bella", "Reksio" – case wont always be perfect but readable)
        if (!el.dataset.fallback) el.dataset.fallback = el.textContent;
        el.textContent = name || el.dataset.fallback;
      });
    }

    // ========== STEP ENGINE ==========
    // Steps: 1 = Q1, 2 = edu1, 3 = Q2, 4 = edu2, 5 = weight → result
    const TOTAL_STEPS = 5;
    const QUESTION_COUNT = document.querySelectorAll('.quiz-step[data-field]').length;
    let currentStep = 1;

    const announcer = document.getElementById('stepAnnouncer');
    const progressFill = document.getElementById('stepProgressFill');
    const progressBar = document.querySelector('.quiz-progress');
    const stepBackBtn = document.getElementById('stepBackBtn');
    const stepTrack = document.getElementById('stepTrack');
    let _advanceTimer = null;
    let _started = false;

    // Progress counts every screen (questions and education). The result sits one
    // short of full – the product page is the last step.
    function setProgress(screenIndex) {
      const pct = Math.round((screenIndex / (TOTAL_STEPS + 2)) * 100);
      progressFill.style.width = `${pct}%`;
      progressBar.setAttribute('aria-valuenow', String(pct));
    }

    // 1. Haptic feedback (10ms micro-vibration, mobile only)
    function triggerHaptic() {
      if (navigator.vibrate) navigator.vibrate(10);
    }

    function startQuiz() {
      if (_started) return;
      const nameInput = document.getElementById('heroDogName');
      const raw = (nameInput?.value || '').trim();
      if (!raw) { nameInput?.focus(); return; }
      triggerHaptic();
      state.dog_name = capitalizeName(raw);
      _started = true;
      track('form_start', { from: 'hero' }, true);
      document.getElementById('hero')?.classList.add('done');
      document.getElementById('start')?.classList.add('quiz-started');
      refreshDogNameMentions();
      goToStep(1, 'forward');
      window.scrollTo({ top: 0 });
    }

    // Back from the first question returns to the name screen
    function backToHero() {
      clearTimeout(_advanceTimer);
      _started = false;
      hideAllScreens();
      document.getElementById('start')?.classList.remove('quiz-started');
      document.getElementById('hero')?.classList.remove('done');
      stepBackBtn.hidden = true;
      setProgress(0);
      announcer.textContent = '';
      window.scrollTo({ top: 0 });
    }

    function hideAllScreens() {
      document.querySelectorAll('.quiz-step').forEach(s => {
        s.classList.remove('active');
        s.setAttribute('hidden', '');
      });
    }

    function showScreen(target) {
      target.removeAttribute('hidden');
      target.classList.remove('exit-left');
      // eslint-disable-next-line no-unused-expressions
      target.offsetWidth;
      target.classList.add('active');
      setTimeout(() => {
        // Focus the heading, not the first option – a focus ring on an option looks like a preselected answer
        const f = target.querySelector('.step-h');
        if (f) {
          f.setAttribute('tabindex', '-1');
          try { f.focus({ preventScroll: true }); } catch { f.focus(); }
        }
      }, 60);
    }

    function goToStep(n, direction) {
      if (n < 1 || n > TOTAL_STEPS) return;
      hideAllScreens();
      const target = document.querySelector(`.quiz-step[data-step="${n}"]`);
      if (!target) return;
      requestAnimationFrame(() => showScreen(target));

      currentStep = n;
      setProgress(n);
      if (target.dataset.field) {
        const qNum = [...document.querySelectorAll('.quiz-step[data-field]')].indexOf(target) + 1;
        announcer.textContent = `Pytanie ${qNum} z ${QUESTION_COUNT}`;
      } else {
        announcer.textContent = '';
      }

      stepBackBtn.hidden = false;
      refreshDogNameMentions();
      refreshNextBtn(n);

      if (target.id === 'sales-edu1') updateSalesEdu1();
      window.scrollTo({ top: 0 });
    }

    function showResult() {
      hideAllScreens();
      stepTrack.style.display = 'none';
      setProgress(TOTAL_STEPS + 1);
      renderSalesResult();
    }

    function hideResult() {
      const report = document.getElementById('report');
      report.classList.remove('visible');
      report.innerHTML = '';
      stepTrack.style.display = '';
    }

    function nextStep() {
      clearTimeout(_advanceTimer);
      if (currentStep < TOTAL_STEPS) {
        goToStep(currentStep + 1, 'forward');
      } else {
        // Last step done – go directly to result
        setTimeout(showResult, 300);
      }
    }
    function prevStep() {
      clearTimeout(_advanceTimer);
      if (document.getElementById('report').classList.contains('visible')) {
        hideResult();
        goToStep(TOTAL_STEPS, 'backward');
      } else if (currentStep > 1) {
        goToStep(currentStep - 1, 'backward');
      } else {
        backToHero();
      }
    }
    stepBackBtn.addEventListener('click', prevStep);

    // ===== Bind option-rows for all single/multi steps =====
    document.querySelectorAll('.quiz-step[data-field]').forEach(stepEl => {
      const field = stepEl.dataset.field;
      const isMulti = stepEl.dataset.multi === 'true';
      const advanceMode = stepEl.dataset.advance; // 'auto' | 'manual'
      const stepN = Number(stepEl.dataset.step);

      stepEl.querySelectorAll('.option-row').forEach(row => {
        row.addEventListener('click', () => {
          triggerHaptic();
          const val = row.dataset.value;
          if (isMulti) {
            // Exclusive options: rows with data-exclusive="true" ("brak …")
            const isExclusive = row.dataset.exclusive === 'true';
            if (isExclusive) {
              stepEl.querySelectorAll('.option-row').forEach(r => {
                r.classList.remove('selected');
                r.setAttribute('aria-pressed', 'false');
              });
              row.classList.add('selected');
              row.setAttribute('aria-pressed', 'true');
              state[field] = [val];
            } else {
              // Clear any exclusive options
              stepEl.querySelectorAll('.option-row[data-exclusive="true"]').forEach(r => {
                r.classList.remove('selected');
                r.setAttribute('aria-pressed', 'false');
              });
              const exclVals = new Set();
              stepEl.querySelectorAll('.option-row[data-exclusive="true"]').forEach(r => exclVals.add(r.dataset.value));
              state[field] = (state[field] || []).filter(v => !exclVals.has(v));
              row.classList.toggle('selected');
              const sel = row.classList.contains('selected');
              row.setAttribute('aria-pressed', sel ? 'true' : 'false');
              if (sel) {
                if (!state[field].includes(val)) state[field].push(val);
              } else {
                state[field] = state[field].filter(v => v !== val);
              }
            }
          } else {
            // Single-select (radio)
            stepEl.querySelectorAll('.option-row').forEach(r => {
              r.classList.remove('selected');
              r.setAttribute('aria-checked', 'false');
            });
            row.classList.add('selected');
            row.setAttribute('aria-checked', 'true');
            state[field] = val;
          }

          // Micro-bounce on the clicked row
          row.classList.remove('just-selected');
          // Force reflow to restart animation
          void row.offsetWidth;
          row.classList.add('just-selected');

          if (advanceMode === 'auto' && !isMulti) {
            // 300ms delay for visual confirmation, then advance
            clearTimeout(_advanceTimer);
            _advanceTimer = setTimeout(() => nextStep(), 300);
          } else {
            refreshNextBtn(stepN);
          }
        });

        // Keyboard: Enter to "click"
        row.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            row.click();
          }
        });
      });
    });

    // ===== "Dalej" buttons =====
    document.querySelectorAll('[data-next]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        triggerHaptic();
        nextStep();
      });
    });

    function refreshNextBtn(stepN) {
      const stepEl = document.querySelector(`.quiz-step[data-step="${stepN}"]`);
      if (!stepEl) return;
      const btn = stepEl.querySelector('[data-next]');
      if (!btn) return;
      let valid = false;
      if (stepEl.dataset.field) {
        const field = stepEl.dataset.field;
        const isMulti = stepEl.dataset.multi === 'true';
        valid = isMulti ? (state[field] || []).length > 0 : !!state[field];
      } else {
        // No field (education screen) – always enabled
        valid = true;
      }
      btn.disabled = !valid;
    }

    // Hero form: name input + start button
    const _heroForm = document.getElementById('heroForm');
    const _heroName = document.getElementById('heroDogName');
    const _heroBtn = document.getElementById('heroStartBtn');
    function capitalizeName(s) {
      if (!s) return '';
      const trimmed = s.trim();
      if (!trimmed) return '';
      return trimmed.charAt(0).toLocaleUpperCase('pl-PL') + trimmed.slice(1);
    }
    if (_heroName && _heroBtn) {
      _heroName.addEventListener('input', () => {
        const v = _heroName.value.trim();
        _heroBtn.disabled = !v;
        state.dog_name = capitalizeName(v);
      });
    }
    if (_heroForm) {
      _heroForm.addEventListener('submit', (e) => { e.preventDefault(); startQuiz(); });
    }

    // ========== HERO NAME PLACEHOLDER (typewriter) ==========
    (function heroPlaceholder() {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const input = document.getElementById('heroDogName');
      if (!input) return;

      const NAMES = ['Mochi', 'Bruno', 'Luna', 'Tola', 'Kuba', 'Mela', 'Figa', 'Reksio'];

      // Reduced motion: static placeholder, skip animation
      if (prefersReducedMotion) {
        input.placeholder = NAMES[0];
        return;
      }

      // ---- Placeholder loop (with focus/blur control) ----
      let placeholderStopped = false;
      let placeholderUserTyped = false;

      async function runPlaceholder() {
        await sleep(1500); // initial delay
        let idx = 0;
        while (!placeholderStopped) {
          const text = NAMES[idx];
          // type
          for (let i = 1; i <= text.length; i++) {
            if (placeholderStopped) return;
            input.setAttribute('placeholder', text.slice(0, i));
            await sleep(80);
          }
          await sleep(2000);
          if (placeholderStopped) return;
          // delete
          for (let i = text.length - 1; i >= 0; i--) {
            if (placeholderStopped) return;
            input.setAttribute('placeholder', text.slice(0, i));
            await sleep(40);
          }
          await sleep(300);
          idx = (idx + 1) % NAMES.length;
        }
      }

      function stopPlaceholder() {
        placeholderStopped = true;
        if (input) input.setAttribute('placeholder', '');
      }
      function maybeResumePlaceholder() {
        if (placeholderUserTyped) return;
        if (input && input.value.trim()) return;
        placeholderStopped = false;
        runPlaceholder();
      }

      if (input) {
        input.addEventListener('focus', stopPlaceholder);
        input.addEventListener('blur', () => {
          if (!input.value.trim()) {
            setTimeout(maybeResumePlaceholder, 1000);
          }
        });
        input.addEventListener('input', () => {
          if (input.value.length > 0) {
            placeholderUserTyped = true;
            placeholderStopped = true;
          } else {
            placeholderUserTyped = false;
          }
        });
      }

      function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

      runPlaceholder();

      // Desktop auto-focus (skip on touch / narrow viewport)
      const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
      const isNarrow = window.innerWidth < 768;
      if (!isTouch && !isNarrow) {
        // Small delay so the page settles first
        setTimeout(() => {
          if (!document.getElementById('start')?.classList.contains('quiz-started')) {
            input.focus({ preventScroll: true });
          }
        }, 600);
      }
    })();

    function escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str == null ? '' : String(str);
      return div.innerHTML;
    }

    // ========== EDU1, RESULT, EMAIL ==========
    // Dosage per weight band (1 pack = 30 bites); the full course is 3 packs = 90 bites
    const DOSE_BY_WEIGHT = { do_10: 1, '10_30': 2, pow_30: 3 };
    const COURSE_BITES = 90;
    const COURSE_DISCOUNT = '33%';

    function dailyDose() { return DOSE_BY_WEIGHT[state.dog_weight] || 1; }
    function courseDays() { return Math.round(COURSE_BITES / dailyDose()); }
    function bitesLabel(n) { return n === 1 ? 'kąsek' : 'kąski'; }

    function hasSkinIssues() {
      return (state.skin_issues || []).some(v => !isNoneSymptom(v));
    }

    function updateSalesEdu1() {
      const h = document.getElementById('salesEdu1Heading');
      const p = document.getElementById('salesEdu1Body');
      if (!h || !p) return;
      const results = `<b>Po 2–4 tygodniach</b> większość opiekunów widzi ${edu1Results()}.`;
      if (hasSkinIssues()) {
        h.innerHTML = 'Zdrowa skóra<br>zaczyna się w jelitach.';
        p.innerHTML = `Drapanie i lizanie łap często mają źródło w jelitach, a nie w skórze. <b>ProBio Kąski</b> wspierają dobre bakterie w jelitach, a przez nie skórę i sierść. ${results}`;
      } else {
        h.innerHTML = 'Spokojna skóra?<br>Utrzymajmy to.';
        p.innerHTML = `Zmiana karmy, antybiotyk albo stres łatwo psują bakterie w jelitach. <b>ProBio Kąski</b> wspierają je na co dzień. ${results}`;
      }
    }

    // Efekty po 2–4 tygodniach (jak na karcie produktu), dobrane do objawów z P1.
    // Kolejność jak na karcie produktu; bez objawów skórnych – sama sierść.
    const EDU1_RESULTS = [
      { text: 'mniej drapania i lizania łap',     for: ['drapanie', 'lizanie_lap'] },
      // „u alergików” tylko gdy zaznaczono alergie – przy samej czerwonej skórze / rankach bez dopisku
      { text: picked => picked.includes('alergie') ? 'mniej podrażnień skóry u alergików' : 'mniej podrażnień skóry',
        for: ['alergie', 'stan_zapalny'] },
      { text: 'gęstszą, lśniącą sierść',          for: ['wypadanie'] },
    ];

    function edu1Results() {
      const picked = state.skin_issues || [];
      let items = EDU1_RESULTS.filter(r => r.for.some(k => picked.includes(k)))
        .map(r => typeof r.text === 'function' ? r.text(picked) : r.text);
      if (!items.length) items = [EDU1_RESULTS[2].text];
      if (items.length < 2) return items[0];
      return items.slice(0, -1).join(', ') + ' oraz ' + items[items.length - 1];
    }

    function primarySkinIssue() {
      const issues = state.skin_issues || [];
      const map = {
        drapanie: 'drapaniem skóry', lizanie_lap: 'lizaniem łap',
        stan_zapalny: 'stanem zapalnym skóry', hotspoty: 'hotspotami',
        wypadanie: 'wypadaniem sierści', alergie: 'alergiami'
      };
      const priority = ['drapanie', 'lizanie_lap', 'stan_zapalny', 'hotspoty', 'wypadanie', 'alergie'];
      for (const p of priority) {
        if (issues.includes(p)) return map[p];
      }
      return null;
    }

    // ?waga= preselects the weight tab in the PDP offer widget (offer-module.js)
    const PDP_WEIGHT_SLUG = { do_10: 'do-10kg', '10_30': '10-30kg', pow_30: 'powyzej-30kg' };

    // Ad tracking parameters the quiz page was opened with – passed on to the product page so the
    // sale stays attributed to the campaign, not to the quiz.
    const PASS_THROUGH_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
      'utm_id', 'fbclid', 'gclid', 'ttclid'];
    const INCOMING_PARAMS = new URLSearchParams(window.location.search);

    function buildSalesPdpUrl(variant) {
      const params = new URLSearchParams();
      if (PDP_WEIGHT_SLUG[state.dog_weight]) params.set('waga', PDP_WEIGHT_SLUG[state.dog_weight]);
      // ?imie= personalises the PDP heading (sections/hero-cta-trustpilot.liquid → "heading_named")
      if ((state.dog_name || '').trim()) params.set('imie', state.dog_name.trim());
      PASS_THROUGH_PARAMS.forEach(key => {
        const value = INCOMING_PARAMS.get(key);
        if (value) params.set(key, value);
      });
      // No campaign UTMs (direct visit, organic link) – mark the traffic as coming from the quiz
      if (!params.has('utm_source')) params.set('utm_source', 'quiz');
      if (!params.has('utm_medium')) params.set('utm_medium', 'sales_quiz');
      // Quiz result variant in its own parameter, so it never overwrites the ad's utm_content
      if (variant) params.set('quiz_wynik', variant);
      return `${SHOP_URL}/products/probiotyk?${params}`;
    }

    // Funnel event with the full quiz answers, sent when the shop CTA is clicked
    function trackQuizCompleted() {
      const payload = {
        quiz_mode: 'sales',
        dog_name: state.dog_name,
        skin_issues: state.skin_issues,
        other_problems: state.other_problems,
        dog_weight: state.dog_weight,
        primary_symptom: primarySkinIssue(),
        result_variant: salesResultVariant(),
        recommended_protocol: `ProBio Kąski 3 opak., ${dailyDose()}/dzień, ${courseDays()} dni`,
        timestamp: Date.now(),
      };
      track('quiz_completed_sales', payload);
    }

    // ===== SALES RESULT COPY (edytowalne – 4 warianty wg wzorca objawów) =====
    // Placeholdery: {dogName}, {symptomsDo} (np. "drapie się, liże łapy i ma zaparcia"),
    // {benefits} (max 3 korzyści, np. "spokojną skórę, zdrowe łapy i regularne wypróżnienia"),
    // {dose} (np. "2 kąski"), {days} (zapas 3 opakowań przy tej dawce), {discount}.
    const SALES_RESULT_SHARED = {
      badge: 'Ukończono',
      // tylko gdy brak objawów
      fit: 'Wynik quizu: <b>ProBio Kąski</b> pasują do Twojego psa. Wspierają dobre bakterie, a przez to pomagają utrzymać {benefits}.',
      symptoms: '{dogName} {symptomsDo}. {symptomsRoot} <b>ProBio Kąski</b> wspierają dobre bakterie, a przez to pomagają utrzymać {benefits}.',
      rootOne: 'Ten objaw często zaczyna się w jelitach.',
      rootMany: 'Te objawy często zaczynają się w jelitach.',
      recommendation: '<b>3 opakowania</b> to zapas na <b>{days} dni</b> ({dose} dziennie).',
      cta: 'Odbierz do {discount} zniżki →',
    };
    const SALES_RESULT_COPY = {
      // Wariant A – tylko objawy skórne
      // benefits = korzyści, którymi dopełniamy listę do 3, gdy objawy dały ich mniej
      skin: {
        heading: '{dogName} jest o krok bliżej spokojnej skóry.',
        benefits: ['spokojną skórę', 'błyszczącą sierść', 'zdrowe jelita'],
      },
      // Wariant B – tylko objawy jelitowe
      gut: {
        heading: '{dogName} jest o krok bliżej spokojnego brzucha.',
        benefits: ['spokojny brzuch', 'dobre trawienie', 'zdrowe jelita'],
      },
      // Wariant C – objawy skórne ORAZ jelitowe
      mixed: {
        heading: '{dogName} jest o krok bliżej spokojnej skóry i brzucha.',
        benefits: ['spokojną skórę', 'spokojny brzuch', 'zdrowe jelita'],
      },
      // Wariant D – brak objawów (profilaktyka)
      none: {
        heading: '{dogName} jest o krok bliżej zdrowych jelit na lata.',
        timeframe: 'Liczy się to, żeby dawać je codziennie.',
        benefits: ['zdrowe jelita', 'spokojną skórę', 'błyszczącą sierść'],
      },
    };

    // Objaw → co robi pies (zdanie z imieniem) i co kąski pomagają utrzymać (biernik, po „utrzymać”).
    // does = czasownik („drapie się”), has = dopełnienie po „ma” – kolejne „ma …” łączymy w jedno.
    const SYMPTOM_FORMS = {
      drapanie:          { does: 'drapie się',                  keeps: 'spokojną skórę' },
      lizanie_lap:       { does: 'liże łapy',                   keeps: 'zdrowe łapy' },
      stan_zapalny:      { has: 'czerwoną skórę lub ranki',     keeps: 'spokojną skórę' },
      wypadanie:         { has: 'matową lub wypadającą sierść', keeps: 'błyszczącą sierść' },
      alergie:           { has: 'alergie',                      keeps: 'spokojną skórę' },
      luzne_stolce:      { has: 'luźne stolce',                 keeps: 'dobre trawienie' },
      zaparcia:          { has: 'zaparcia',                     keeps: 'regularne wypróżnienia' },
      jedzenie_trawy:    { does: 'je trawę',                    keeps: 'spokojny brzuch' },
      silne_gazy:        { has: 'gazy',                         keeps: 'spokojny brzuch' },
      potrzasanie_glowa: { does: 'potrząsa głową',              keeps: 'zdrowe uszy' },
    };

    // Korzyści z objawów (bez powtórek), dopełnione domyślnymi wariantu – zawsze 3
    function resultBenefits(keys, fallback) {
      const all = [...keys.map(k => SYMPTOM_FORMS[k].keeps), ...fallback];
      return [...new Set(all)].slice(0, 3);
    }

    // "a, b i c"
    function joinPL(items) {
      if (items.length < 2) return items[0] || '';
      return items.slice(0, -1).join(', ') + ' i ' + items[items.length - 1];
    }

    // "drapie się, liże łapy i ma zaparcia" / "drapie się, ma alergie i zaparcia"
    function symptomsDoes(keys) {
      const forms = keys.map(k => SYMPTOM_FORMS[k]);
      const does = forms.filter(f => f.does).map(f => f.does);
      const has = forms.filter(f => f.has).map(f => f.has);
      if (has.length >= 2) return [...does, 'ma ' + joinPL(has)].join(', ');
      return joinPL(has.length ? [...does, 'ma ' + has[0]] : does);
    }

    function isNoneSymptom(v) {
      return v === 'brak_skory' || v === 'brak_innych' || v === 'nic';
    }

    function salesResultVariant() {
      const skin = (state.skin_issues || []).filter(v => !isNoneSymptom(v)).length;
      const gut  = (state.other_problems || []).filter(v => !isNoneSymptom(v)).length;
      if (skin > 0 && gut > 0) return 'mixed'; // Wariant C
      if (gut > 0)             return 'gut';   // Wariant B
      if (skin > 0)            return 'skin';  // Wariant A
      return 'none';                           // Wariant D
    }

    function renderSalesResult() {
      const name = dogName();
      const variant = salesResultVariant();
      const pdpUrl = buildSalesPdpUrl(variant);

      const copy = { ...SALES_RESULT_SHARED, ...SALES_RESULT_COPY[variant] };
      const skinSel = (state.skin_issues || []).filter(v => !isNoneSymptom(v));
      const gutSel  = (state.other_problems || []).filter(v => !isNoneSymptom(v));
      // Max 3 objawy łącznie: skórne, potem jelitowe – przy obu grupach zawsze min. 1 jelitowy
      const skinKeys = skinSel.filter(k => SYMPTOM_FORMS[k]);
      const gutKeys  = gutSel.filter(k => SYMPTOM_FORMS[k]);
      const shown = [...skinKeys.slice(0, gutKeys.length ? 2 : 3), ...gutKeys].slice(0, 3);
      const dose = dailyDose();

      // Interpolacja: {dogName} → wyróżnione imię, {symptoms*} → odmienione objawy
      const nameSpan = `<span class="dog-name">${escapeHtml(name)}</span>`;
      const fill = (s) => s
        .replace(/{dogName}/g, nameSpan)
        .replace(/{symptomsDo}/g, escapeHtml(symptomsDoes(shown)))
        .replace(/{symptomsRoot}/g, shown.length === 1 ? copy.rootOne : copy.rootMany)
        .replace(/{benefits}/g, escapeHtml(joinPL(resultBenefits(shown, copy.benefits))))
        .replace(/{dose}/g, `${dose} ${bitesLabel(dose)}`)
        .replace(/{days}/g, String(courseDays()))
        .replace(/{discount}/g, COURSE_DISCOUNT);


      const report = document.getElementById('report');
      report.innerHTML = `
        <div class="sales-result">
          <span class="result-badge"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>${escapeHtml(copy.badge)}</span>
          <h1 class="result-greet">${fill(copy.heading)}</h1>
          <p class="sales-result-body">${shown.length ? fill(copy.symptoms) : fill(copy.fit)}${copy.timeframe ? ' ' + fill(copy.timeframe) : ''}</p>
          <p class="result-reco">${fill(copy.recommendation)}</p>
          <a href="${escapeHtml(pdpUrl)}" class="product-cta" id="salesPdpCta" rel="noopener">${fill(copy.cta)}</a>
          <img src="${escapeHtml(quizAsset('wynik-pole.jpg'))}" alt="Szczęśliwa właścicielka z jamnikiem i ProBio Kąski" class="result-photo"
               onerror="this.style.display='none'">
        </div>
      `;
      report.classList.add('visible');
      track('report_shown', { mode: 'sales' }, true);

      // Wire PDP CTA – fire event on click
      const pdpCta = document.getElementById('salesPdpCta');
      if (pdpCta) {
        pdpCta.addEventListener('click', () => {
          trackQuizCompleted();
          track('cta_click', { target: 'sales_pdp' });
        });
      }

      window.scrollTo({ top: 0 });
    }

    // ========== INIT ==========
    refreshDogNameMentions();
    // Make sure step 1 is the only visible step at boot (hero showing)
    document.querySelectorAll('.quiz-step').forEach(s => {
      if (s.dataset.step === '1') {
        s.classList.add('active');
        s.removeAttribute('hidden');
      } else {
        s.classList.remove('active');
        s.setAttribute('hidden', '');
      }
    });
    refreshNextBtn(1);
